<template>
  <div v-if="open" class="ap-overlay">
    <header class="ap-topbar">
      <div class="ap-topbar-left">
        <span class="ap-mode-badge">PREVIEW</span>
        <span class="ap-title">{{ assessment.title || 'Untitled assessment' }}</span>
      </div>
      <div class="ap-topbar-right">
        <span class="ap-progress" v-if="!finished">Question {{ currentIndex + 1 }} / {{ total }}</span>
        <button class="ap-close" @click="close">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          Exit preview
        </button>
      </div>
    </header>

    <main class="ap-body">
      <!-- Empty state -->
      <div v-if="total === 0" class="ap-empty">
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" style="color:var(--muted-3)"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        <div class="ap-empty-title">No questions to preview</div>
        <div class="ap-empty-sub">Add at least one question to the assessment to start a preview run.</div>
        <button class="btn-primary" style="margin-top:14px" @click="close">Back to editor</button>
      </div>

      <!-- Active question -->
      <div v-else-if="!finished && currentQ" class="ap-card">
        <div class="ap-q-head">
          <span class="ap-q-type" :class="currentQ.type">
            {{ currentQ.type === 'multi' ? 'Multiple choice' : 'Single choice' }}
          </span>
          <span class="ap-q-points">{{ currentQ.points }} pt{{ currentQ.points === 1 ? '' : 's' }}</span>
        </div>

        <div class="ap-q-prompt">{{ currentQ.prompt || '(empty prompt)' }}</div>

        <div class="ap-options">
          <div v-for="(opt, i) in currentQ.options" :key="opt.id"
               class="ap-option"
               :class="optionClass(i)"
               @click="toggleOption(i)">
            <div class="ap-option-letter">
              <template v-if="isMulti">
                <svg v-if="isOptSelected(i)" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                <span v-else>{{ letters[i] }}</span>
              </template>
              <template v-else>{{ letters[i] }}</template>
            </div>
            <div class="ap-option-text">{{ opt.text || `Option ${letters[i]}` }}</div>
          </div>
        </div>

        <transition name="ap-fade">
          <div v-if="checked" class="ap-feedback" :class="{ correct: isCorrect, wrong: !isCorrect }">
            <div class="ap-feedback-head">
              <svg v-if="isCorrect" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg>
              <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              <b>{{ isCorrect ? 'Correct' : 'Incorrect' }}</b>
              <span v-if="!isCorrect" class="ap-feedback-sub">
                Correct answer: <b>{{ correctLetters }}</b>
              </span>
            </div>
            <div v-if="!isCorrect && currentQ.explanation" class="ap-feedback-body">
              {{ currentQ.explanation }}
            </div>
          </div>
        </transition>

        <div class="ap-actions">
          <button class="btn-ghost" :disabled="currentIndex === 0" @click="goPrev">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="15 18 9 12 15 6"/></svg>
            Previous
          </button>
          <button v-if="!checked" class="btn-primary" :disabled="!hasSelection" @click="checkAnswer">Check answer</button>
          <button v-else-if="currentIndex < total - 1" class="btn-primary" @click="goNext">
            Next
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
          <button v-else class="btn-primary" @click="finish">Finish preview</button>
        </div>
      </div>

      <!-- Score screen -->
      <div v-else-if="finished" class="ap-result">
        <div class="ap-result-ring" :class="{ pass, fail: !pass }">
          <div class="ap-result-ring-pct">{{ Math.round(scorePct) }}%</div>
          <div class="ap-result-ring-label">{{ pass ? 'Pass' : 'Below passing' }}</div>
        </div>
        <div class="ap-result-headline">
          You answered <b>{{ correctCount }}</b> of <b>{{ total }}</b> correctly.
        </div>
        <div class="ap-result-meta">
          Passing threshold: <b>{{ assessment.passingScore }}%</b>
        </div>
        <div class="ap-result-actions">
          <button class="btn-ghost" @click="restart">Restart</button>
          <button class="btn-primary" @click="close">Back to editor</button>
        </div>

        <!-- Per-question summary -->
        <div class="ap-result-list">
          <div v-for="(q, i) in assessment.questions" :key="q.id" class="ap-result-row">
            <span class="ap-result-num">{{ String(i + 1).padStart(2, '0') }}</span>
            <span class="ap-result-icon" :class="{ ok: results[i] && results[i].correct, bad: results[i] && !results[i].correct, skip: !results[i] }">
              <svg v-if="results[i] && results[i].correct" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg>
              <svg v-else-if="results[i]" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              <svg v-else width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="5" y1="12" x2="19" y2="12"/></svg>
            </span>
            <span class="ap-result-text">{{ q.prompt || `Question ${i + 1}` }}</span>
            <span class="ap-result-jump" @click="jumpTo(i)">Review</span>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
export default {
  name: 'AssessmentPreview',
  data() {
    return {
      letters: ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'],
      currentIndex: 0,
      // selections: { [qIndex]: number | number[] }
      selections: {},
      // results: { [qIndex]: { correct: boolean } }
      results: {},
      finished: false,
    };
  },
  computed: {
    open() { return this.$store.state.ui.previewOpen; },
    assessment() { return this.$store.state.assessment; },
    total() { return this.assessment.questions.length; },
    currentQ() { return this.assessment.questions[this.currentIndex] || null; },
    isMulti() { return this.currentQ && this.currentQ.type === 'multi'; },
    currentSel() {
      const v = this.selections[this.currentIndex];
      if (this.isMulti) return Array.isArray(v) ? v : [];
      return typeof v === 'number' ? v : null;
    },
    hasSelection() {
      const v = this.currentSel;
      return this.isMulti ? Array.isArray(v) && v.length > 0 : typeof v === 'number';
    },
    checked() { return !!this.results[this.currentIndex]; },
    isCorrect() { return this.checked && this.results[this.currentIndex].correct; },
    correctLetters() {
      if (!this.currentQ) return '';
      const idx = this.currentQ.options.map((o, i) => o.correct ? i : -1).filter(i => i >= 0);
      return idx.map(i => this.letters[i]).join(', ') || '—';
    },
    correctCount() {
      return Object.values(this.results).filter(r => r && r.correct).length;
    },
    scorePct() {
      if (this.total === 0) return 0;
      const earned = this.assessment.questions.reduce((sum, q, i) => {
        const r = this.results[i];
        return sum + (r && r.correct ? (q.points || 1) : 0);
      }, 0);
      const max = this.assessment.questions.reduce((s, q) => s + (q.points || 1), 0);
      return max > 0 ? (earned / max) * 100 : 0;
    },
    pass() { return this.scorePct >= (this.assessment.passingScore || 0); },
  },
  watch: {
    open(val) {
      if (val) {
        this.restart();
        document.addEventListener('keydown', this.onKey);
      } else {
        document.removeEventListener('keydown', this.onKey);
      }
    },
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKey);
  },
  methods: {
    close() { this.$store.commit('CLOSE_PREVIEW'); },
    restart() {
      this.currentIndex = 0;
      this.selections = {};
      this.results = {};
      this.finished = false;
    },
    isOptSelected(i) {
      if (this.isMulti) return Array.isArray(this.currentSel) && this.currentSel.includes(i);
      return this.currentSel === i;
    },
    toggleOption(i) {
      if (this.checked) return;
      if (this.isMulti) {
        const cur = Array.isArray(this.selections[this.currentIndex])
          ? this.selections[this.currentIndex].slice()
          : [];
        const at = cur.indexOf(i);
        if (at >= 0) cur.splice(at, 1);
        else cur.push(i);
        cur.sort((a, b) => a - b);
        this.$set(this.selections, this.currentIndex, cur);
      } else {
        this.$set(this.selections, this.currentIndex, i);
      }
    },
    optionClass(i) {
      const selected = this.isOptSelected(i);
      if (!this.checked) return { selected };
      const isCorrectOpt = !!this.currentQ.options[i].correct;
      return {
        selected,
        'is-correct': isCorrectOpt,
        'is-wrong': selected && !isCorrectOpt,
      };
    },
    checkAnswer() {
      if (!this.hasSelection) return;
      const opts = this.currentQ.options;
      const correctSet = opts.map((o, i) => o.correct ? i : -1).filter(i => i >= 0).sort();
      let isCorrect;
      if (this.isMulti) {
        const a = (this.currentSel || []).slice().sort();
        isCorrect = a.length === correctSet.length && a.every((v, i) => v === correctSet[i]);
      } else {
        isCorrect = correctSet.length === 1 && correctSet[0] === this.currentSel;
      }
      this.$set(this.results, this.currentIndex, { correct: isCorrect });
    },
    goPrev() { if (this.currentIndex > 0) this.currentIndex--; },
    goNext() { if (this.currentIndex < this.total - 1) this.currentIndex++; },
    jumpTo(i) {
      if (i < 0 || i >= this.total) return;
      this.currentIndex = i;
      this.finished = false;
    },
    finish() { this.finished = true; },
    onKey(e) {
      if (this.finished || !this.currentQ) {
        if (e.key === 'Escape') this.close();
        return;
      }
      const key = e.key.toUpperCase();
      const idx = this.letters.indexOf(key);
      if (idx >= 0 && idx < this.currentQ.options.length) {
        e.preventDefault();
        this.toggleOption(idx);
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        if (!this.checked && this.hasSelection) this.checkAnswer();
        else if (this.checked) {
          if (this.currentIndex < this.total - 1) this.goNext();
          else this.finish();
        }
      } else if (e.key === 'ArrowRight' && this.checked) this.goNext();
      else if (e.key === 'ArrowLeft') this.goPrev();
      else if (e.key === 'Escape') this.close();
    },
  },
};
</script>

<style scoped>
.ap-overlay {
  position: fixed; inset: 0;
  background: var(--bg);
  display: flex; flex-direction: column;
  z-index: 500;
}
.ap-topbar {
  height: 52px;
  background: var(--brand-navy);
  color: #fff;
  display: flex; align-items: center;
  padding: 0 22px;
  flex-shrink: 0;
}
.ap-topbar-left, .ap-topbar-right {
  display: flex; align-items: center; gap: 12px;
}
.ap-topbar-right { margin-left: auto; }
.ap-mode-badge {
  background: var(--accent);
  color: #fff;
  font-size: 10px; font-weight: 800;
  letter-spacing: 0.1em;
  padding: 4px 10px;
  border-radius: 4px;
}
.ap-title { font-size: 14px; font-weight: 600; }
.ap-progress { font-family: var(--font-mono); font-size: 12px; color: var(--brand-navy-muted); }
.ap-close {
  display: inline-flex; align-items: center; gap: 6px;
  background: rgba(255,255,255,0.1); color: #fff;
  border: none; padding: 6px 12px;
  border-radius: 6px;
  font-size: 12.5px; font-weight: 600;
  cursor: pointer;
  transition: background 0.13s;
}
.ap-close:hover { background: rgba(255,255,255,0.18); }

.ap-body {
  flex: 1;
  overflow: auto;
  padding: 36px 24px;
  display: flex; justify-content: center;
}

.ap-empty {
  align-self: center;
  text-align: center;
  color: var(--muted);
  display: flex; flex-direction: column; align-items: center;
}
.ap-empty-title { font-size: 16px; font-weight: 600; color: var(--ink-3); margin-top: 8px; }
.ap-empty-sub { font-size: 13px; margin-top: 4px; }

.ap-card {
  width: 100%; max-width: 720px;
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 26px 30px;
  box-shadow: var(--shadow);
}
.ap-q-head { display: flex; align-items: center; gap: 10px; margin-bottom: 16px; }
.ap-q-type {
  font-size: 10px; font-weight: 800;
  letter-spacing: 0.06em;
  padding: 3px 8px;
  border-radius: 4px;
  text-transform: uppercase;
}
.ap-q-type.single { background: var(--accent-soft); color: var(--accent-dark); }
.ap-q-type.multi  { background: #ede9fe; color: #6d28d9; }
.ap-q-points {
  font-size: 11px; color: var(--muted);
  font-family: var(--font-mono);
  margin-left: auto;
}
.ap-q-prompt {
  font-size: 16px; font-weight: 600;
  color: var(--ink); line-height: 1.55;
  margin-bottom: 22px;
}

.ap-options { display: flex; flex-direction: column; gap: 8px; margin-bottom: 22px; }
.ap-option {
  display: grid; grid-template-columns: 28px 1fr; gap: 14px;
  align-items: center;
  padding: 12px 16px;
  border: 1.5px solid var(--border);
  border-radius: 8px;
  background: var(--card);
  cursor: pointer;
  transition: all 0.13s;
}
.ap-option:hover { border-color: var(--muted-3); background: var(--bg-alt); }
.ap-option.selected { border-color: var(--accent); background: var(--accent-soft); }
.ap-option.is-correct { border-color: #10b981; background: #ecfdf5; }
.ap-option.is-wrong   { border-color: #ef4444; background: #fef2f2; }
.ap-option-letter {
  width: 26px; height: 26px;
  border-radius: 6px;
  border: 1.5px solid var(--border);
  display: grid; place-items: center;
  font-size: 12.5px; font-weight: 700;
  color: var(--muted);
  background: var(--card);
  font-family: var(--font-mono);
}
.ap-option.selected .ap-option-letter { background: var(--accent); border-color: var(--accent); color: #fff; }
.ap-option.is-correct .ap-option-letter { background: #10b981; border-color: #10b981; color: #fff; }
.ap-option.is-wrong   .ap-option-letter { background: #ef4444; border-color: #ef4444; color: #fff; }
.ap-option-text { font-size: 14px; color: var(--ink); line-height: 1.45; }

.ap-feedback {
  padding: 12px 16px;
  border-radius: 10px;
  border: 1px solid;
  font-size: 13.5px;
  margin-bottom: 18px;
}
.ap-feedback.correct { background: #ecfdf5; border-color: #a7f3d0; color: #065f46; }
.ap-feedback.wrong   { background: #fef2f2; border-color: #fecaca; color: #7f1d1d; }
.ap-feedback-head { display: flex; align-items: center; gap: 8px; font-weight: 700; }
.ap-feedback-sub { font-weight: 500; font-size: 12.5px; margin-left: auto; opacity: 0.85; }
.ap-feedback-body { margin-top: 8px; line-height: 1.55; }

.ap-actions { display: flex; justify-content: space-between; align-items: center; padding-top: 14px; border-top: 1px solid var(--border-soft); }

.ap-result {
  width: 100%; max-width: 640px;
  align-self: center; text-align: center;
}
.ap-result-ring {
  width: 140px; height: 140px;
  border-radius: 50%;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  margin: 0 auto 18px;
  border: 6px solid var(--accent);
  background: var(--card);
}
.ap-result-ring.pass { border-color: #10b981; }
.ap-result-ring.fail { border-color: #ef4444; }
.ap-result-ring-pct {
  font-size: 36px; font-weight: 800;
  color: var(--ink);
  letter-spacing: -0.03em;
  font-variant-numeric: tabular-nums;
}
.ap-result-ring-label {
  font-size: 11px; font-weight: 700; letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--muted);
  margin-top: 2px;
}
.ap-result-headline { font-size: 16px; color: var(--ink); }
.ap-result-headline b { font-weight: 700; }
.ap-result-meta { font-size: 13px; color: var(--muted); margin-top: 4px; }
.ap-result-actions { display: flex; gap: 10px; justify-content: center; margin-top: 22px; }

.ap-result-list {
  margin-top: 28px;
  text-align: left;
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 10px;
  overflow: hidden;
}
.ap-result-row {
  display: grid; grid-template-columns: 32px 24px 1fr auto; gap: 12px;
  align-items: center;
  padding: 10px 14px;
  border-bottom: 1px solid var(--border-soft);
  font-size: 13px;
  color: var(--ink);
}
.ap-result-row:last-child { border-bottom: none; }
.ap-result-num { font-family: var(--font-mono); color: var(--muted); font-size: 11.5px; }
.ap-result-icon {
  width: 22px; height: 22px;
  border-radius: 6px;
  display: grid; place-items: center;
  background: var(--bg);
  color: var(--muted);
}
.ap-result-icon.ok   { background: #d1fae5; color: #047857; }
.ap-result-icon.bad  { background: #fee2e2; color: #b91c1c; }
.ap-result-icon.skip { background: var(--bg); color: var(--muted-2); }
.ap-result-text {
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.ap-result-jump {
  font-size: 12px; font-weight: 600;
  color: var(--accent-dark);
  cursor: pointer;
}
.ap-result-jump:hover { text-decoration: underline; }

.ap-fade-enter-active, .ap-fade-leave-active { transition: opacity 0.18s ease, transform 0.18s ease; }
.ap-fade-enter, .ap-fade-leave-to { opacity: 0; transform: translateY(-4px); }

@media (max-width: 768px) {
  .ap-overlay { padding: 0; }
  .ap-card    { max-width: 100%; border-radius: 0; min-height: 100vh; min-height: 100dvh; }
  /* Tighten the inline grids inside the preview at narrow widths. */
  .ap-card .ap-row {
    grid-template-columns: 28px 1fr !important;
    gap: 10px !important;
  }
}
</style>
